import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, User, Phone, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthProvider';
import { dashboardPathForRole } from '../lib/roles';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { PasswordField } from '../components/auth/PasswordField';
import { Illustration } from '../components/common/Illustration';
import { completePhoneSignIn, startPhoneSignIn } from '../lib/firebase/phoneAuth';

export function RegisterScreen() {
  const { loginWithPhone, register } = useAuth();
  const navigate = useNavigate();

  // Registration Mode: Mobile Number (OTP) or Password
  const [showEmailRegister, setShowEmailRegister] = useState(false);

  // Mobile OTP signup states (Primary)
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpConfirmation, setOtpConfirmation] = useState<
    | { mode: 'web'; confirmation: import('firebase/auth').ConfirmationResult }
    | { mode: 'native'; verificationId: string }
    | null
  >(null);
  const [otpLoading, setOtpLoading] = useState(false);

  // Email / Password signup states (Secondary)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Send OTP handler for registration
  const handleSendOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError('Please enter your full name first.');
      return;
    }
    setError(null);
    setSuccess(null);
    setOtpLoading(true);
    try {
      const result = await startPhoneSignIn(mobile);
      if (result.mode === 'native' && result.verificationId === '__AUTO__') {
        const idToken = await completePhoneSignIn(result, '');
        const me = await loginWithPhone(idToken, name);
        navigate(dashboardPathForRole(me.role), { replace: true });
        return;
      }
      setOtpConfirmation(result);
      setSuccess(`Verification code sent to ${mobile.trim().length === 10 ? '+91' + mobile.trim() : mobile.trim()}`);
    } catch (err: any) {
      console.error('Error sending OTP:', err);
      setError(err.message || 'Failed to send OTP. Please check the number and try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!otpConfirmation) return;
    setError(null);
    setSuccess(null);
    setOtpLoading(true);
    try {
      const idToken = await completePhoneSignIn(otpConfirmation, otpCode);
      const me = await loginWithPhone(idToken, name);
      navigate(dashboardPathForRole(me.role), { replace: true });
    } catch (err: any) {
      console.error('Error verifying OTP:', err);
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Email Signup handler
  const onSubmitEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setOtpLoading(true);
    try {
      const me = await register(name, email, password);
      navigate(dashboardPathForRole(me.role), { replace: true });
    } catch (err: any) {
      setError(err.message || 'Could not create your account.');
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <section className="adaptive-page auth-screen">
      <div className="auth-card glass-surface">
        <Illustration context="onboarding.auth" size="sm" style={{ margin: '0 auto 16px auto', display: 'block', maxHeight: '130px' }} />
        <span className="section-kicker">Get started free</span>
        <h1>Create your account</h1>
        <p>Join thousands of aspirants preparing smarter with WebFoxx Academy.</p>

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}
        {success && (
          <div className="auth-success" role="alert" style={{ background: 'rgba(46, 184, 46, 0.1)', color: '#2eb82e', padding: '10px 14px', borderRadius: '6px', fontSize: '0.9rem', marginBottom: '16px', border: '1px solid rgba(46, 184, 46, 0.2)' }}>
            {success}
          </div>
        )}

        {!showEmailRegister ? (
          /* Primary Mobile OTP Registration Form */
          !otpConfirmation ? (
            <form className="auth-form" onSubmit={handleSendOtp}>
              <label className="auth-field">
                <User size={17} aria-hidden="true" />
                <span className="sr-only">Full name</span>
                <input
                  type="text"
                  required
                  placeholder="Full name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="auth-field">
                <Phone size={17} aria-hidden="true" />
                <span className="sr-only">Mobile Number</span>
                <input
                  type="tel"
                  required
                  placeholder="Mobile Number (e.g. 9999999999)"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/[^\d+]/g, ''))}
                />
              </label>
              <button type="submit" className="primary-action" disabled={otpLoading}>
                {otpLoading ? 'Sending OTP…' : 'Send Verification OTP'}
                {!otpLoading && <ArrowRight size={18} aria-hidden="true" />}
              </button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={handleVerifyOtp}>
              <label className="auth-field">
                <Lock size={17} aria-hidden="true" />
                <span className="sr-only">Verification Code</span>
                <input
                  type="text"
                  maxLength={6}
                  required
                  placeholder="6-Digit OTP Code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  style={{ letterSpacing: otpCode ? '4px' : 'normal', textAlign: otpCode ? 'center' : 'left' }}
                />
              </label>
              <button type="submit" className="primary-action" disabled={otpLoading || otpCode.length !== 6}>
                {otpLoading ? 'Signing up…' : 'Verify & Create Account'}
                {!otpLoading && <ArrowRight size={18} aria-hidden="true" />}
              </button>
              <button
                type="button"
                className="secondary-action"
                onClick={() => {
                  setOtpConfirmation(null);
                  setOtpCode('');
                  setError(null);
                  setSuccess(null);
                }}
                style={{ marginTop: '8px', width: '100%', display: 'flex', justifyContent: 'center' }}
              >
                Change Details
              </button>
            </form>
          )
        ) : (
          /* Secondary Fallback Email Registration Form */
          <form className="auth-form" onSubmit={onSubmitEmail}>
            <label className="auth-field">
              <User size={17} aria-hidden="true" />
              <span className="sr-only">Full name</span>
              <input
                type="text"
                required
                placeholder="Full name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="auth-field">
              <Mail size={17} aria-hidden="true" />
              <span className="sr-only">Email</span>
              <input
                type="email"
                required
                placeholder="you@example.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <PasswordField
              label="Password"
              placeholder="Create a password (min 6 chars)"
              autoComplete="new-password"
              minLength={6}
              value={password}
              onChange={setPassword}
            />
            <button type="submit" className="primary-action" disabled={otpLoading}>
              {otpLoading ? 'Creating account…' : 'Create account'}
              {!otpLoading && <ArrowRight size={18} aria-hidden="true" />}
            </button>
          </form>
        )}

        <div style={{ margin: '16px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            type="button"
            className="secondary-action"
            onClick={() => {
              setShowEmailRegister(!showEmailRegister);
              setError(null);
              setSuccess(null);
            }}
            style={{ width: '100%', display: 'flex', justifyContent: 'center', fontSize: '0.85rem' }}
          >
            {showEmailRegister ? 'Sign up with Mobile Number (OTP)' : 'Sign up with Password instead'}
          </button>
        </div>

        <GoogleSignInButton
          onSignedIn={(me) => navigate(dashboardPathForRole(me.role), { replace: true })}
        />

        <div className="auth-links">
          <span>
            Already have an account? <Link to="/login">Log in</Link>
          </span>
        </div>
      </div>

      {/* reCAPTCHA Anchor Container */}
      <div id="recaptcha-container"></div>
    </section>
  );
}
